import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  DIMENSIONS,
  ARCHETYPES,
  REGROUPER,
  buildReport,
} from '@air/shared';
import { Masthead } from '../components/Masthead';
import { Eyebrow, DimensionIcon, NdcLogo } from '../components/atoms';
import { SignalMeter, Emblem, Quadrant } from '../components/visualizations';
import { T, fonts, layout } from '../theme/tokens';

const DIMENSION_DESCRIPTIONS = [
  {
    key: 'vision',
    label: 'Vision',
    overview: 'Vision is about intent, not knowledge. It asks whether you know what you actually want AI to do for your business, separate from what\'s technically possible. Plenty of organisations adopt AI because everyone else seems to be, and that\'s the weakest possible reason. Urgency isn\'t universal. The right answer for one firm is "move now" and for another it\'s "get the foundations right first," and both can be correct. What matters is that the direction is yours, specific to your business, and genuinely shared. A vision held by one enthusiastic leader while the rest of the team nods along isn\'t a vision, it\'s a liability waiting for its first setback.',
    prompts: 'Can your leadership team each describe, in one sentence, what AI should change about the business? Would those sentences match? Is your reason for adopting AI specific to your situation, or is it mostly that the market feels like it\'s moving?',
  },
  {
    key: 'awareness',
    label: 'Awareness',
    overview: 'Awareness is your grasp of the art of the possible. Not what AI can do in a demo, but what it could realistically do in your business, in your processes, with your data. Most leaders are running on secondhand information, headlines, vendor pitches and competitor claims, which is why AI feels simultaneously urgent and vague. The fix is to flip the direction of the question. Instead of starting from AI\'s capabilities and looking for somewhere to apply them, start from your real pain points and ask which ones AI could help with. The genuine opportunities are rarely exotic. They\'re usually the high-volume, repetitive, information-heavy work that quietly eats your skilled people\'s time.',
    prompts: 'What are your leadership conversations about AI actually based on, evidence from your own use or noise from outside? Could you name three specific places in your business where AI would help, and three where it wouldn\'t? When did you last see AI applied in a business genuinely like yours?',
  },
  {
    key: 'capability',
    label: 'Capability',
    overview: 'Capability is the difference between having AI and being good at it. Licences owned and tools installed tell you almost nothing. What matters is whether AI is woven into real work, whether your people can reliably get good results from it, and how far those results actually go. There\'s a ladder here worth knowing. At the bottom, AI is a novelty that saves a few minutes. In the middle, it genuinely speeds up real tasks. At the top, it changes how work gets done and lets you do things you simply couldn\'t before. Most organisations sit far lower on that ladder than they believe, because scattered personal use feels like capability while producing very little of it. Skill with AI is also not something that spreads on its own. In the firms that have it, it was deliberately built.',
    prompts: 'Is AI use in your organisation shared and repeatable, or private and personal? Where does your usage sit on the ladder, saving minutes or changing how work gets done? If your most capable AI users left tomorrow, what would remain?',
  },
  {
    key: 'foundations',
    label: 'Foundations',
    overview: 'Foundations are what AI has to work with, your processes, your data and your ownership. This dimension carries the single most important idea in the whole assessment. AI amplifies what you have. Point it at a clear, consistent, documented process and it multiplies the value. Point it at tribal knowledge and messy data and it scales the mess faster than you\'ve ever scaled anything. The great majority of AI initiative failures are operational rather than technological. The technology worked. The organisation wasn\'t ready to be amplified. The encouraging part is that foundations work is unglamorous, well understood and entirely within your control. Document how the work actually happens rather than how the manual says it happens, standardise the core, and make sure someone owns every process that matters.',
    prompts: 'Could a capable new starter follow your most important process from written guidance alone? Would you trust an AI tool that relied on your business data for something important? When something goes wrong, is it clear who owns the fix?',
  },
  {
    key: 'people',
    label: 'People & Change',
    overview: 'This dimension covers the human experience of AI, and it\'s the one organisations most consistently underestimate. As AI takes over more of the doing, your people\'s value shifts toward framing, judging and deciding. That shift is bigger than it sounds. It removes the lighter, routine tasks that used to pace a working day, leaving people in near-constant high-stakes judgement, and the resulting tiredness is real even when the workload looks unchanged. There\'s also a collaboration trap. AI makes individuals feel self-sufficient, so sharing thins out and AI becomes a competitive event inside the company rather than a team sport. Individual productivity is not organisational progress. And one more honest truth that almost nobody says aloud. Not everyone wants a more judgement-heavy role. Some people genuinely value the routine work AI removes, and pretending everyone will simply adapt is how good people get lost.',
    prompts: 'Is the personal productivity your people feel actually adding up to collective progress? Have you created any regular way for people to share what they\'re learning with AI? Have you honestly asked who in your organisation wants the new kind of work, and who doesn\'t?',
  },
  {
    key: 'capacity',
    label: 'Capacity',
    overview: 'Capacity is the forgotten constraint. Conversations about AI readiness obsess over capability, skills and tools, and almost never ask the simpler question of whether anyone actually has the time to lead this. Firms with strong vision, decent foundations and willing people still get nowhere when adoption is everyone\'s enthusiasm and nobody\'s job, because if AI is nobody\'s actual job it gets done at nobody\'s actual standard. Bandwidth is also the one constraint that overrides all the others. It doesn\'t matter how ready you are on every other dimension if the honest answer to "who leads this" is "whoever has a spare evening." Sometimes the genuinely right advice is that this isn\'t your moment to push, and the first step is making space rather than choosing technology.',
    prompts: 'Who, by name, owns AI adoption in your organisation? What came off their plate to make room for it? If the answer to either question is unclear, you\'ve found your real starting point.',
  },
  {
    key: 'value',
    label: 'Value Discipline',
    overview: 'Value discipline is the habit of tying AI effort to outcomes that matter, and it\'s what separates progress from motion. AI generates enormous amounts of activity, and activity is seductive. The pattern we keep seeing is impressive-looking projects, clever arrangements of tools, workflows and agents, that cannot answer one simple question. What business result has this actually delivered? When the answer is a demo and a lot of enthusiasm, you\'re funding motion rather than progress, and motion is the most expensive kind of standing still. The discipline is to start from the outcome and work backwards, to write down what success looks like before any work begins, and to be willing to retire things that are interesting but idle. Busy is not the same as valuable.',
    prompts: 'Pick your most impressive AI project and ask what measurable result it has delivered. When someone builds or automates something with AI, is the why established before the work starts? What would you retire tomorrow if you judged everything purely on results?',
  },
  {
    key: 'workforce',
    label: 'Workforce Model',
    overview: 'The workforce model dimension asks whether your organisation has reckoned with how AI rewires skills, roles and career progression. This is the question almost nobody has touched, and it has the longest shadow. AI takes over the foundational work first, and that\'s the same work your junior people have always learned the trade on. So where does your next generation of senior people come from? The traditional training ground is vanishing, the path from junior to senior is changing shape, and the roles you\'ll hire for in three years may not look like the ones you hire for today. Meanwhile a new kind of AI-native professional is emerging who works in genuinely different ways. None of this is urgent this quarter, which is exactly why it gets ignored. It is far cheaper to think through now than to react to later, and the organisations that work it out early will have a lasting advantage in the talent that matters most.',
    prompts: 'If AI absorbed most of your entry-level work next year, how would your people learn the trade? Are your career paths and progression criteria still describing the work as it\'s actually changing? Who in your organisation owns this question, or is it currently nobody\'s?',
  },
];

// Styles - Light theme for both web and PDF
const wrap = {
  minHeight: "100vh",
  background: T.cream,
  color: T.textDark,
  fontFamily: fonts.body,
  fontWeight: 400,
  padding: "clamp(18px,4vw,52px)",
  boxSizing: "border-box",
};

const shell = { maxWidth: layout.containerNarrow, margin: "0 auto" };

const h2Style = {
  fontFamily: fonts.display,
  fontWeight: 700,
  fontSize: "clamp(22px,3.6vw,30px)",
  lineHeight: 1.15,
  letterSpacing: "-1px",
  margin: 0,
  color: T.textDark,
};

const leadStyle = { fontSize: 17, lineHeight: 1.85, color: T.textBody, fontWeight: 400 };

const cardStyle = {
  background: T.surface,
  border: `1px solid ${T.borderLight}`,
  borderRadius: layout.radius,
  padding: "22px 22px",
  borderLeft: `${layout.borderAccent} solid ${T.accent}`,
};

export function ReportPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isPdf = searchParams.get('pdf') === 'true';

  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAssessment() {
      try {
        const res = await fetch(`/api/assessment/${id}`);
        if (!res.ok) throw new Error('Assessment not found');
        const data = await res.json();
        setAssessment(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchAssessment();
  }, [id]);

  if (loading) {
    return (
      <div style={wrap}>
        <div style={shell}>
          <Masthead />
          <p style={{ color: T.textMid, marginTop: 40 }}>Loading report...</p>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div style={wrap}>
        <div style={shell}>
          <Masthead />
          <p style={{ color: T.accent, marginTop: 40 }}>{error || 'Assessment not found'}</p>
        </div>
      </div>
    );
  }

  // Reconstruct result object from stored assessment
  const result = {
    c: assessment.criterionScores,
    ax: {
      direction100: assessment.direction100,
      readiness100: assessment.readiness100,
    },
    mom: assessment.momentum,
    base: assessment.archetype,
    posture: assessment.posture,
    postureSource: assessment.postureSource,
    flag: assessment.capacityFlag,
    grade: assessment.grade,
    gradeName: assessment.gradeName,
    reconcile: null,
  };

  const report = buildReport({
    result,
    sector: assessment.sector,
    size: assessment.size,
    expFollowup: assessment.expFollowup,
    regrouperConfirmed: assessment.regrouperConfirmed,
  });

  const R = report;
  const archetypeName = R.isRegrouper ? 'Regrouping' : (ARCHETYPES[result.base]?.name || '');

  // Light theme wrapper for PDF
  const pdfWrap = isPdf ? {
    background: T.cream,
    color: T.textDark,
    fontFamily: fonts.body,
    fontWeight: 400,
    minHeight: '100vh',
  } : wrap;

  const pdfShell = isPdf ? {
    maxWidth: 720,
    margin: '0 auto',
    padding: '40px 50px',
  } : shell;

  return (
    <div style={pdfWrap}>
      {/* PDF Title Page */}
      {isPdf && (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 40px',
          pageBreakAfter: 'always',
          background: T.cream,
        }}>
          <NdcLogo size={18} style={{ marginBottom: 40 }} />

          <h1 style={{
            fontFamily: fonts.display,
            fontWeight: 800,
            fontSize: 42,
            lineHeight: 1.1,
            letterSpacing: '-1.5px',
            textAlign: 'center',
            margin: '0 0 20px',
            color: T.textDark,
          }}>
            AI Readiness Review
          </h1>

          <p style={{
            fontFamily: fonts.body,
            fontSize: 18,
            fontWeight: 400,
            color: T.textBody,
            textAlign: 'center',
            maxWidth: 460,
            lineHeight: 1.7,
            margin: '0 0 50px',
          }}>
            Your personalised assessment across eight dimensions of AI readiness
          </p>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            marginBottom: 30,
          }}>
            <Emblem kind={R.isRegrouper ? 'regrouper' : result.base} size={80} />
            <div>
              <div style={{
                fontFamily: fonts.display,
                fontWeight: 700,
                fontSize: 28,
                letterSpacing: '-0.5px',
                color: T.textDark,
              }}>
                {archetypeName}
              </div>
              <div style={{
                fontFamily: fonts.body,
                fontSize: 13,
                color: T.textMid,
                marginTop: 4,
              }}>
                Grade: {result.gradeName}
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: 40,
            marginTop: 20,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 32, letterSpacing: '-2px', color: T.accent }}>
                {result.ax.direction100}%
              </div>
              <div style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: T.textMid, letterSpacing: '2px', textTransform: 'uppercase' }}>
                DIRECTION
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: fonts.display, fontWeight: 800, fontSize: 32, letterSpacing: '-2px', color: T.accent }}>
                {result.ax.readiness100}%
              </div>
              <div style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: T.textMid, letterSpacing: '2px', textTransform: 'uppercase' }}>
                READINESS
              </div>
            </div>
          </div>

          <div style={{
            position: 'absolute',
            bottom: 60,
            fontFamily: fonts.body,
            fontSize: 11,
            color: T.textLight,
          }}>
            Prepared for {assessment.email}
          </div>
        </div>
      )}

      {/* Main Report Content */}
      <div style={pdfShell}>
        {!isPdf && <Masthead />}

        <Eyebrow>Your full report</Eyebrow>
        <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
          <Emblem kind={R.isRegrouper ? 'regrouper' : result.base} size={76} />
          <h2 style={h2Style}>{R.heading}</h2>
        </div>
        {R.teaser.map((p, i) => <p key={i} style={{ ...leadStyle, fontSize: 16, marginTop: i === 0 ? 16 : 14 }}>{p}</p>)}

        <div style={{ ...cardStyle, marginTop: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16, flexWrap: "wrap", gap: 6 }}>
            <span style={{ fontFamily: fonts.body, fontSize: 11, fontWeight: 600, color: T.accent, letterSpacing: "2px", textTransform: "uppercase" }}>Your archetype</span>
            <span style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 16, color: T.accent }}>{archetypeName}</span>
          </div>
          <SignalMeter c={result.c} />
          <div style={{ display: "flex", gap: 24, alignItems: "center", marginTop: 24, flexWrap: "wrap" }}>
            <div style={{ flex: "0 0 auto" }}>
              <Quadrant d100={result.ax.direction100} r100={result.ax.readiness100} label={archetypeName} />
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.55, margin: 0, fontStyle: "italic" }}>{R.signalNote}</p>
            </div>
          </div>
        </div>

        {isPdf && <div style={{ pageBreakBefore: 'always' }} />}

        <div style={{
          marginTop: isPdf ? 40 : 30,
          padding: "28px 26px",
          borderRadius: layout.radius,
          background: T.accent,
          color: "#fff",
        }}>
          <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 22, letterSpacing: "-0.5px", margin: "0 0 12px", color: "#fff" }}>You could be here</h3>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.7, color: "rgba(255,255,255,0.92)" }}>{R.couldBe}</p>
        </div>

        <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 21, letterSpacing: "-0.5px", marginTop: isPdf ? 40 : 30, marginBottom: 14, color: T.textDark }}>{R.themesHeading}</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {R.themes.map((t, i) => {
            const dim = DIMENSIONS.find((d) => d.key === t.dim);
            const iconKey = dim?.key || "capability";
            return (
              <div key={i} style={{ ...cardStyle, pageBreakInside: 'avoid' }}>
                <div style={{ display: "flex", gap: 11, alignItems: "center", marginBottom: 7 }}>
                  <DimensionIcon dimension={iconKey} size={18} color={T.accent} style={{ flexShrink: 0, opacity: 0.4 }} />
                  <span style={{ fontFamily: fonts.display, fontSize: 24, fontWeight: 800, color: T.accent, opacity: 0.4, letterSpacing: "-2px" }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 16.5, lineHeight: 1.25, letterSpacing: "-0.3px", color: T.textDark }}>{t.title}</span>
                </div>
                <p style={{ margin: 0, fontSize: 14, color: T.textMid, lineHeight: 1.6 }}>{t.body}</p>
              </div>
            );
          })}
        </div>

        {/* CTA box with spacer that travels with it when pushed to new page */}
        <div style={{ pageBreakInside: 'avoid' }}>
          <div style={{ height: 40 }} />
          <div style={{
            padding: "26px 24px",
            borderRadius: layout.radius,
            background: T.creamDark,
            borderLeft: `${layout.borderAccent} solid ${T.accent}`,
          }}>
            <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 22, letterSpacing: "-0.5px", margin: "0 0 10px", color: T.textDark }}>This is the 5-minute read. {R.cta.line}</h3>
            <p style={{ ...leadStyle, fontSize: 15, marginTop: 0, marginBottom: 0 }}>{R.cta.body} If this resonated, we'll be in touch to explore what it means for you.</p>
          </div>
        </div>

        {/* Dimension Descriptions Section - PDF only */}
        {isPdf && (
          <>
            <div style={{ pageBreakBefore: 'always' }} />
            <h3 style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 22, letterSpacing: "-0.5px", marginTop: 40, marginBottom: 20, color: T.textDark }}>The Eight Dimensions of AI Readiness</h3>
            <p style={{ ...leadStyle, fontSize: 15, marginTop: 0, marginBottom: 24, maxWidth: 600 }}>
              Your assessment measured eight distinct criteria. Here's what each one means for your organisation.
            </p>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {DIMENSION_DESCRIPTIONS.map((dim, i) => (
                <div key={i} style={{ pageBreakInside: 'avoid' }}>
                  {/* Spacer before box - travels with box when pushed to new page */}
                  <div style={{ height: i === 0 ? 0 : 36 }} />
                  <div style={cardStyle}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                      <DimensionIcon dimension={dim.key} size={20} color={T.accent} style={{ flexShrink: 0 }} />
                      <span style={{ fontFamily: fonts.display, fontWeight: 700, fontSize: 16, letterSpacing: "-0.3px", color: T.textDark }}>{dim.label}</span>
                    </div>
                    <p style={{ margin: '0 0 12px', fontSize: 13.5, color: T.textBody, lineHeight: 1.6, fontWeight: 400 }}>
                      {dim.overview}
                    </p>
                    <div style={{
                      background: `${T.accent}12`,
                      borderRadius: layout.radius,
                      padding: '12px 14px',
                      marginTop: 8,
                    }}>
                      <div style={{
                        fontFamily: fonts.body,
                        fontSize: 10,
                        fontWeight: 600,
                        color: T.accent,
                        letterSpacing: '2px',
                        marginBottom: 6,
                        textTransform: 'uppercase',
                      }}>
                        Worth looking at
                      </div>
                      <p style={{ margin: 0, fontSize: 12.5, color: T.textDark, lineHeight: 1.55, fontStyle: 'italic', fontWeight: 400 }}>
                        {dim.prompts}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {!isPdf && (
          <p style={{ textAlign: "center", marginTop: 24, fontFamily: fonts.body, fontSize: 12, color: T.textLight }}>
            neil@neildunlopconsulting.com | neildunlopconsulting.com
          </p>
        )}
      </div>

      {/* PDF Final Page */}
      {isPdf && (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 40px',
          pageBreakBefore: 'always',
          background: T.cream,
        }}>
          <NdcLogo size={22} style={{ marginBottom: 50 }} />

          <p style={{
            fontFamily: fonts.body,
            fontSize: 18,
            fontWeight: 400,
            color: T.textBody,
            textAlign: 'center',
            maxWidth: 480,
            lineHeight: 1.7,
            margin: '0 0 40px',
          }}>
            Helping organisations cut through the noise and build AI capability that actually delivers.
          </p>

          <div style={{
            background: T.surface,
            border: `1px solid ${T.borderLight}`,
            borderLeft: `${layout.borderAccent} solid ${T.accent}`,
            borderRadius: layout.radius,
            padding: '30px 40px',
            textAlign: 'center',
            marginBottom: 50,
          }}>
            <div style={{
              fontFamily: fonts.display,
              fontWeight: 700,
              fontSize: 20,
              letterSpacing: '-0.5px',
              color: T.textDark,
              marginBottom: 12,
            }}>
              What happens next?
            </div>
            <p style={{
              fontFamily: fonts.body,
              fontSize: 15,
              fontWeight: 400,
              color: T.textBody,
              margin: 0,
              lineHeight: 1.6,
            }}>
              We'll be in touch to explore what this means for your organisation and where to focus first.
            </p>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}>
            <div style={{
              fontFamily: fonts.body,
              fontSize: 13,
              color: T.textDark,
            }}>
              neil@neildunlopconsulting.com
            </div>
            <div style={{
              fontFamily: fonts.body,
              fontSize: 13,
              color: T.textMid,
            }}>
              neildunlopconsulting.com
            </div>
          </div>

          <div style={{
            position: 'absolute',
            bottom: 40,
            fontFamily: fonts.body,
            fontSize: 10,
            color: T.textLight,
          }}>
            Generated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        </div>
      )}
    </div>
  );
}
